import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-light-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/pro-light-svg-icons/faChevronUp';
import { faPen } from '@fortawesome/pro-regular-svg-icons/faPen';
import React, { useCallback } from 'react';
import classnames from 'classnames';
import { FlexRow } from 'ui-components/components/flex';
import { CompletedStepCheckmark } from 'ui-components/components/completed-step-checkmark/completed-step-checkmark';
import { Text, TextType } from 'ui-components/components/text';
import { Button } from 'ui-components/components/button';
import { SetupConfigurationConfirmSaveDialogue } from './setup-configurations/setup-configuration-confirm-save-dialogue';
import { useAdvancedShippingNoticeContext } from '../advanced-shipping-notice-context';
import { useModal } from '../../../../../../hooks/use-modal';

import { messages } from './messages';
import style from './step-card-title.module.less';
import { ORDERED_ASN_STEPS } from './constants';
import { useSelectedProductEntities } from './select-products/hooks';

/**
 * Component for the title of the steps in creating an advanced shipping notification.
 * @param {object} props
 * @param {string} props.stepId - ID from the step constants file
 * @param {object} props.stepPayload - Payload to be submitted on save
 * @param {object} props.titleMessageDescriptor - Message Descriptor for the title
 * @param {boolean} props.isCollapsed
 * @param {(boolean) => void} props.setIsCollapsed
 * @returns {JSX.Element}
 */
export const StepCardTitle = ({ stepId, stepPayload, titleMessageDescriptor, isCollapsed = false, setIsCollapsed }) => {
  const {
    isEditingStepWithId,
    editStepWithId,
    saveStepWithId,
    isCompleteStepWithId,
    isDraft,
    isSavingStep,
    doesSaveRequireClearDefineShipments,
  } = useAdvancedShippingNoticeContext();
  const { isOpen, open, close } = useModal();
  const selectedProductEntities = useSelectedProductEntities(stepPayload);

  const onClickEditStep = useCallback(() => editStepWithId(stepId), [editStepWithId, stepId]);
  const onSave = useCallback(
    () => saveStepWithId(stepId, stepPayload, selectedProductEntities),
    [saveStepWithId, stepId, stepPayload, selectedProductEntities],
  );
  const conditionallyRenderSaveSpeedbump = useCallback(() => {
    if (doesSaveRequireClearDefineShipments) {
      open();
    } else {
      onSave();
    }
  }, [doesSaveRequireClearDefineShipments, onSave, open]);
  const toggleCollapse = useCallback(
    (e) => {
      if (!e?.defaultPrevented) {
        setIsCollapsed(!isCollapsed);
      }
    },
    [isCollapsed, setIsCollapsed],
  );

  const isEditing = isEditingStepWithId(stepId);
  const isComplete = isCompleteStepWithId(stepId) && !isEditing;
  const chevronIcon = isCollapsed ? faChevronDown : faChevronUp;

  const index = ORDERED_ASN_STEPS.indexOf(stepId) + 1;

  return (
    <FlexRow className={classnames(style['title-wrapper'], !isDraft && style.pointer)} onClick={toggleCollapse}>
      <FlexRow className={style['step-title-with-check']}>
        {isDraft && <CompletedStepCheckmark isComplete={isComplete} />}
        <Text formattedMessageValues={{ index: isDraft ? `${index}. ` : '' }} textType={TextType.H3} value={titleMessageDescriptor} />
      </FlexRow>
      {!isDraft && <FontAwesomeIcon className={style.chevron} icon={chevronIcon} />}
      {isDraft && (
        <>
          {isComplete && (
            <Button className={style['edit-button']} onClick={onClickEditStep} theme='link'>
              <FontAwesomeIcon className={style['edit-svg']} icon={faPen} />
              <Text textType={TextType.Paragraph} value={messages.edit} />
            </Button>
          )}
          {isEditing && (
            <FlexRow className={style['space-gap']}>
              <Button
                buttonSize='small'
                disabled={isSavingStep}
                isLoading={isSavingStep}
                onClick={conditionallyRenderSaveSpeedbump}
                theme='primary'
              >
                <Text textType={TextType.Paragraph} value={messages.save} />
              </Button>
            </FlexRow>
          )}
        </>
      )}
      {isOpen && <SetupConfigurationConfirmSaveDialogue onClose={close} onConfirm={onSave} />}
    </FlexRow>
  );
};
